import { Component, OnDestroy, OnInit } from '@angular/core'
import {
    AdminClient, CrmAssignee, NearestParking, Parking, ParkingStatus, Prospect,
    ProspectSource, ProspectSourceLabels, ProspectStatus, ProspectStatusColors,
    ProspectStatusLabels, ProspectType, ProspectTypeLabels,
} from '@parkupp/core'
import { firstValueFrom, Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { MapTypeStyle } from '@agm/core/services/google-maps-types'
import { ParkingService } from 'src/app/services/parking.service'
import { AdminClientService } from 'src/app/services/admin-client.service'
import { ProspectingService } from 'src/app/services/prospecting.service'
import { ProspectModalService } from '../prospect-modal/prospect-modal.service'

const MAP_VIEW_STATE_KEY = 'parkupp_admin_prospecting_map_view'

interface StatusConfig {
    label: string
    color: string
    visible: boolean
    count: number
}

@Component({
    selector: 'app-admin-prospecting-map',
    templateUrl: './admin-prospecting-map.component.html',
    styleUrls: ['./admin-prospecting-map.component.scss'],
})
export class AdminProspectingMapComponent implements OnInit, OnDestroy {
    initialLat = -33.9249
    initialLng = 18.4241
    initialZoom = 11

    private viewLat = this.initialLat
    private viewLng = this.initialLng
    private viewZoom = this.initialZoom

    mapStyles: MapTypeStyle[] = [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    ]

    allProspects: Prospect[] = []
    displayProspects: Prospect[] = []
    publishedParkings: Parking[] = []
    unpublishedParkings: Parking[] = []
    displayPublishedParkings: Parking[] = []
    displayUnpublishedParkings: Parking[] = []
    showPublishedParkings = true
    showUnpublishedParkings = true
    loading = true

    selectedParking: Parking | null = null

    showClosedLeads = false
    closedLeads: AdminClient[] = []
    displayClosedLeads: AdminClient[] = []
    closedLeadsLoaded = false
    closedLeadIconUrl = ''
    selectedClosedLead: AdminClient | null = null
    selectedClosedLeadNearestParkings: NearestParking[] = []

    private mapBounds: { south: number; north: number; west: number; east: number } | null = null
    private boundsChange$ = new Subject<any>()
    private destroy$ = new Subject<void>()

    assigneeFilter = ''
    sourceFilter = ''
    searchTermFilter = ''
    prospectTypeFilter = ''
    assignees: CrmAssignee[] = []
    sourceOptions = Object.values(ProspectSource)
    sourceLabels = ProspectSourceLabels
    searchTermOptions: string[] = []
    prospectTypeOptions = Object.values(ProspectType)
    prospectTypeLabels = ProspectTypeLabels

    // Dropdown panel open state
    parkingsDropdownOpen = false
    prospectsDropdownOpen = false

    statusConfig: { [key: string]: StatusConfig } = {
        [ProspectStatus.NEW]: { label: ProspectStatusLabels[ProspectStatus.NEW], color: ProspectStatusColors[ProspectStatus.NEW], visible: true, count: 0 },
        [ProspectStatus.PROSPECTING]: { label: ProspectStatusLabels[ProspectStatus.PROSPECTING], color: ProspectStatusColors[ProspectStatus.PROSPECTING], visible: true, count: 0 },
        [ProspectStatus.CONTACTED]: { label: ProspectStatusLabels[ProspectStatus.CONTACTED], color: ProspectStatusColors[ProspectStatus.CONTACTED], visible: true, count: 0 },
        [ProspectStatus.NEGOTIATING]: { label: ProspectStatusLabels[ProspectStatus.NEGOTIATING], color: ProspectStatusColors[ProspectStatus.NEGOTIATING], visible: true, count: 0 },
        [ProspectStatus.CLIENT]: { label: ProspectStatusLabels[ProspectStatus.CLIENT], color: ProspectStatusColors[ProspectStatus.CLIENT], visible: true, count: 0 },
        [ProspectStatus.ARCHIVED]: { label: ProspectStatusLabels[ProspectStatus.ARCHIVED], color: ProspectStatusColors[ProspectStatus.ARCHIVED], visible: false, count: 0 },
    }
    statusKeys = Object.keys(this.statusConfig)
    iconUrlMap: { [key: string]: string } = {}
    publishedParkingIconUrl = ''
    unpublishedParkingIconUrl = ''

    constructor(private parkingService: ParkingService, private adminClientService: AdminClientService, private prospecting: ProspectingService, private modal: ProspectModalService) {
        this.restoreMapView()
        this.boundsChange$.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe((bounds: any) => {
            const ne = bounds.getNorthEast()
            const sw = bounds.getSouthWest()
            this.mapBounds = { south: sw.lat(), north: ne.lat(), west: sw.lng(), east: ne.lng() }
            this.refreshDisplay()
        })
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    async ngOnInit(): Promise<void> {
        this.publishedParkingIconUrl = this.buildPinSvg('#22c55e')
        this.unpublishedParkingIconUrl = this.buildPinSvg('#f59e0b')
        this.prospecting.listProspectingAssignees().subscribe((a) => (this.assignees = a))
        try {
            const parkings = await firstValueFrom(this.parkingService.listAll())
            const withLocation = parkings.filter((p) => p.parkingLocation?.latitude && p.parkingLocation?.longitude)
            this.publishedParkings = withLocation.filter((p) => p.parkingStatus === ParkingStatus.PUBLISHED)
            this.unpublishedParkings = withLocation.filter((p) => p.parkingStatus !== ParkingStatus.PUBLISHED)
        } catch (e) {
            console.error('Error loading parkings:', e)
        }

        try {
            const prospects = await this.prospecting.listProspects()
            this.allProspects = prospects.filter((p) => p.latitude && p.longitude)
            this.searchTermOptions = [...new Set(this.allProspects.map((p) => p.searchTerm).filter(Boolean) as string[])].sort()
            this.computeCounts()
            this.refreshDisplay()
        } catch (e) {
            console.error('Error loading prospects:', e)
        } finally {
            this.loading = false
        }
    }

    onCenterChange(center: google.maps.LatLngLiteral): void {
        this.viewLat = center.lat
        this.viewLng = center.lng
        this.persistMapView()
    }

    onZoomChange(zoom: number): void {
        this.viewZoom = zoom
        this.persistMapView()
    }

    private computeCounts(): void {
        for (const key of this.statusKeys) this.statusConfig[key].count = 0
        for (const p of this.allProspects) {
            if (this.statusConfig[p.status]) this.statusConfig[p.status].count++
        }
    }

    toggleStatus(key: string): void {
        this.statusConfig[key].visible = !this.statusConfig[key].visible
        this.refreshDisplay()
    }

    togglePublishedParkings(): void {
        this.showPublishedParkings = !this.showPublishedParkings
    }

    toggleUnpublishedParkings(): void {
        this.showUnpublishedParkings = !this.showUnpublishedParkings
    }

    onBoundsChange(bounds: any): void {
        this.boundsChange$.next(bounds)
    }

    applyFilters(): void {
        this.refreshDisplay()
    }

    private inBounds(lat?: number, lng?: number): boolean {
        if (!this.mapBounds || lat == null || lng == null) return true
        const b = this.mapBounds
        return lat >= b.south && lat <= b.north && lng >= b.west && lng <= b.east
    }

    private refreshDisplay(): void {
        this.displayProspects = this.allProspects.filter((p) => {
            if (!this.inBounds(p.latitude, p.longitude)) return false
            if (!this.statusConfig[p.status]?.visible) return false
            if (this.assigneeFilter && p.assignedToUid !== this.assigneeFilter) return false
            if (this.sourceFilter && p.source !== this.sourceFilter) return false
            if (this.searchTermFilter && p.searchTerm !== this.searchTermFilter) return false
            if (this.prospectTypeFilter && p.prospectType !== this.prospectTypeFilter) return false
            return true
        })
        this.displayPublishedParkings = this.publishedParkings.filter((p) => this.inBounds(p.parkingLocation?.latitude, p.parkingLocation?.longitude))
        this.displayUnpublishedParkings = this.unpublishedParkings.filter((p) => this.inBounds(p.parkingLocation?.latitude, p.parkingLocation?.longitude))
        this.displayClosedLeads = this.closedLeads.filter((l) => this.inBounds(l.latitude, l.longitude))
        this.generateMarkerIcons()
    }

    private generateMarkerIcons(): void {
        Object.values(this.iconUrlMap).forEach((url) => URL.revokeObjectURL(url))
        this.iconUrlMap = {}
        for (const p of this.displayProspects) {
            if (!p.$key) continue
            const color = this.statusConfig[p.status]?.color || '#6b7280'
            const blob = new Blob([this.buildDotSvg(color)], { type: 'image/svg+xml' })
            this.iconUrlMap[p.$key] = URL.createObjectURL(blob)
        }
    }

    private buildDotSvg(color: string): string {
        return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
            <circle cx="9" cy="9" r="7" fill="${color}" stroke="#fff" stroke-width="2"/>
        </svg>`
    }

    private buildPinSvg(color: string): string {
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="38" viewBox="0 0 28 40">
            <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
            <circle cx="14" cy="14" r="6" fill="#fff"/>
        </svg>`
        const blob = new Blob([svg], { type: 'image/svg+xml' })
        return URL.createObjectURL(blob)
    }

    onProspectClick(p: Prospect): void {
        if (p.$key) this.modal.open(p.$key)
    }

    onParkingClick(parking: Parking): void {
        this.selectedParking = parking
    }

    onParkingInfoClose(): void {
        this.selectedParking = null
    }

    async toggleClosedLeads(): Promise<void> {
        this.showClosedLeads = !this.showClosedLeads
        if (this.showClosedLeads && !this.closedLeadsLoaded) {
            try {
                this.closedLeads = await this.adminClientService.listClosedNoAvailabilityLeads()
                this.closedLeadIconUrl = this.buildClosedLeadIcon()
                this.closedLeadsLoaded = true
                this.refreshDisplay()
            } catch (e) {
                console.error('Error loading closed leads:', e)
            }
        }
    }

    onClosedLeadClick(lead: AdminClient): void {
        this.selectedClosedLead = lead
        if (lead.latitude && lead.longitude) {
            const allParkings = [...this.publishedParkings, ...this.unpublishedParkings]
            this.selectedClosedLeadNearestParkings = this.prospecting.computeNearestParkings(lead.latitude, lead.longitude, allParkings)
        } else {
            this.selectedClosedLeadNearestParkings = []
        }
    }

    onClosedLeadInfoClose(): void {
        this.selectedClosedLead = null
    }

    private buildClosedLeadIcon(): string {
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
            <circle cx="9" cy="9" r="7" fill="#8b0000" stroke="#fff" stroke-width="2"/>
        </svg>`
        const blob = new Blob([svg], { type: 'image/svg+xml' })
        return URL.createObjectURL(blob)
    }

    parkingAddress(parking: Parking): string {
        const loc = parking.parkingLocation
        if (!loc) return ''
        return [loc.suburb, loc.city, loc.province].filter(Boolean).join(', ')
    }

    private restoreMapView(): void {
        try {
            const saved = localStorage.getItem(MAP_VIEW_STATE_KEY)
            if (!saved) return
            const parsed = JSON.parse(saved)
            if (typeof parsed.lat === 'number') this.initialLat = parsed.lat
            if (typeof parsed.lng === 'number') this.initialLng = parsed.lng
            if (typeof parsed.zoom === 'number') this.initialZoom = parsed.zoom
            this.viewLat = this.initialLat
            this.viewLng = this.initialLng
            this.viewZoom = this.initialZoom
        } catch {}
    }

    private persistMapView(): void {
        try {
            localStorage.setItem(MAP_VIEW_STATE_KEY, JSON.stringify({ lat: this.viewLat, lng: this.viewLng, zoom: this.viewZoom }))
        } catch {}
    }
}
